import { useEffect, useState, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Grid, TextField, Typography, Alert, Button, CircularProgress, Stack } from '@mui/material'
import { Page, PageTitle, SelectLocation, SelectCategory, SelectScanMode, ActionBar, Sheet, Lightbox, ScanVerdict, QuantityStepper } from '../../components'
import { isValidQuantity } from '../../components/QuantityStepper'
import { LOCATIONS } from '../../constants'
import useFetch from '../../hooks/useFetch'
import InlineLoader from '../../loading/InlineLoader'
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BarcodeScanner, { isCameraAvailable } from '../../components/BarcodeScanner'
import { tokens } from '../../theme'
import Chip from '@mui/material/Chip'
import { ALL_CATEGORIES } from '../../components/SelectCategory'
import Box from '@mui/material/Box'

const defaultProduct = {
    metal: '',
    price: null,
    imageUrl: '',
    quantity: 0,
    barcode: null
}

export default function InventoryCheck(props) {
    const { setAuth } = props;
    const { t } = useTranslation();
    const { isPending, fetchResource, showAlert, alertMessage, setAlertMessage, setShowAlert } = useFetch({ setAuth });
    const inputRef = useRef();
    const [location, setLocation] = useState('');
    const [showChangeLocation, setShowChangeLocation] = useState(false);
    const [barcode, setBarcode] = useState('');
    const [multipleQuantity, setMultipleQuantity] = useState('0');
    const [showBarcodeEntry, setShowBarcodeEntry] = useState(false);
    const [scannedProduct, setScannedProduct] = useState(defaultProduct);
    const [showScannedProduct, setShowScannedProduct] = useState(false);
    const [alreadyScanned, setAlreadyScanned] = useState(false);
    const [productHasMultiple, setProductHasMultiple] = useState(false);
    const [finishedScanning, setFinishedScanning] = useState(false);
    const [unscannedProducts, setUnscannedProducts] = useState([]);
    // The list shrinks as pieces are answered, so its length cannot say how far along
    // the clerk is. The total is captured once, when the list arrives.
    const [unscannedTotal, setUnscannedTotal] = useState(0);
    const [showCompleted, setShowCompleted] = useState(false);
    const [showConfirmFinishedScanning, setShowConfirmFinishedScanning] = useState(false);
    const [loadingImage, setLoadingImage] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const [image, setImage] = useState(null);
    // Three states, and they are all distinct: null means no category has been chosen
    // yet, '' means the clerk chose the uncategorized scope, and a string names one.
    // localStorage.getItem draws exactly that line, so seed straight from it.
    const [productType, setProductType] = useState(() => localStorage.getItem('productType'));
    const [categories, setCategories] = useState([]);
    const [showChangeCategory, setShowChangeCategory] = useState(false);
    // Seeded from storage for the same reason as productType: effects run after paint,
    // so a mode read only in the mount effect flashes the empty state on every refresh.
    const [scanMode, setScanMode] = useState(() => localStorage.getItem('scanMode') || '');
    const [showChangeMode, setShowChangeMode] = useState(false);
    const [totals, setTotals] = useState(null);
    const [outOfScope, setOutOfScope] = useState(null);
    // The already-counted screen's second-piece path. It is not a separate scan result,
    // so it is not derived from the response -- it is a step the clerk chose to take.
    const [countingMore, setCountingMore] = useState(false);
    const [showCameraScanner, setShowCameraScanner] = useState(false);
    const bufferRef = useRef('');
    const timerRef = useRef(null);
    // The window listener below is registered once with [] deps, so it cannot read
    // isPending directly -- its closure would hold the mount-time value forever and the
    // guard would silently never fire. A ref written on each render is what makes the
    // current value reachable from inside that stale closure.
    const scanPendingRef = useRef(false);
    scanPendingRef.current = isPending('scan');

    // != null, never a truthy test: '' is the uncategorized scope, a run like any other.
    const runEstablished = scanMode === 'all' || (scanMode === 'category' && productType != null);

    // One name for what the screen is currently saying, so the render does not have to
    // re-derive it from four booleans in three places. Order matters: out of scope wins
    // because nothing was recorded, and countingMore is a step taken from `already`.
    const verdict = !showScannedProduct ? null
        : outOfScope ? 'outOfScope'
        : countingMore ? 'countMore'
        : productHasMultiple ? 'multiple'
        : alreadyScanned ? 'already'
        : 'counted';

    // null is the whole store, '' the uncategorized scope, anything else its own name.
    const scopeLabel = scope => scope == null ? t('inventoryCheck.allCategories') : scope || t('inventoryCheck.noCategory');

    // Progress for whatever is being counted, in the same (scanned/total) shape the
    // category picker uses. `null` scope means the whole store; anything else, including
    // '', names a category row in the categories response.
    const scopeProgress = scope => {
        const source = scope == null
            ? totals
            : (categories ?? []).find(row => row.productType === scope);
        if (!source) return '';
        const count = location === 'newark' ? source.newarkCount : source.longbranchCount;
        const scanned = location === 'newark' ? source.newarkScanned : source.longbranchScanned;
        return count == null ? '' : ` (${scanned ?? 0}/${count})`;
    };

    // Choosing what to count starts a fresh pass. Neither a completion screen nor an
    // unscanned list from a previous scope may survive into the new one.
    function beginRun() {
        reset();
        setUnscannedProducts([]);
    }

    function saveLocation(locationToSet) {
        localStorage.setItem('location', locationToSet);
        setLocation(locationToSet);
        setShowChangeLocation(false);
        reset();
        // A run is scoped to location + what is being counted, so a new location
        // means asking again. Without this the clerk is never asked and every scan
        // silently falls back to the whole catalogue.
        localStorage.removeItem('scanMode');
        setScanMode('');
        // The category goes with it. Left behind, the picker opens already showing it,
        // and MUI fires onChange only on a CHANGE - so re-picking it records nothing and
        // the clerk is stuck between a dead Select and "Nenhuma contagem selecionada".
        localStorage.removeItem('productType');
        setProductType(null);
        setShowChangeMode(true);
    }

    async function loadCategories() {
        try {
            const response = await fetchResource({ resource: 'inventory-check/categories', loadingKey: 'categories' });
            setCategories(response.categories || []);
            setTotals(response.totals || null);
        } catch (error) {
            setCategories([]);
            setTotals(null);
        }
    }

    // Selecting a scope records it and nothing else. Clearing scan flags happens only
    // at finalize, so no choice a clerk makes here can destroy a count.
    function saveCategory(categoryToSet) {
        // "All" comes from the same picker but is not a category: it is the
        // whole-store scope, so it takes chooseMode('all')'s path rather than
        // being stored as a productType. Offering it here is what lets a clerk
        // already mid-count widen without backing out to the mode sheet.
        if (categoryToSet === ALL_CATEGORIES) {
            setShowChangeCategory(false);
            localStorage.setItem('scanMode', 'all');
            setScanMode('all');
            localStorage.removeItem('productType');
            setProductType(null);
            loadCategories();
            return beginRun();
        }

        localStorage.setItem('productType', categoryToSet);
        setProductType(categoryToSet);
        // Picking a category IS counting by category, and it is the moment the run
        // becomes real - so this, not chooseMode, is where the mode is recorded.
        localStorage.setItem('scanMode', 'category');
        setScanMode('category');
        setShowChangeCategory(false);
        beginRun();
    }

    function chooseMode(mode) {
        setShowChangeMode(false);
        // Counting by category is not a run until a category is picked. Recording the
        // mode here would let a cancelled picker drop an established run and strand the
        // clerk in a category picker with no way back to the whole store.
        if (mode === 'category') {
            // Clear the old category before opening the picker. MUI skips onChange when the
            // clicked value equals the current one, so a picker that opens already showing
            // the clerk's previous category makes re-picking it a dead click -- no mode is
            // recorded and the only way out is a different category or Cancel. Starting on
            // the neutral placeholder makes every option a change.
            localStorage.removeItem('productType');
            setProductType(null);
            loadCategories();
            return setShowChangeCategory(true);
        }
        localStorage.setItem('scanMode', mode);
        setScanMode(mode);
        localStorage.removeItem('productType');
        setProductType(null);
        loadCategories();
        beginRun();
    }

    function handleBarcodeInput(e) {
        const { value } = e.target;
        if (!/^\d*$/.test(value)) return
        if (value < 0 || value.length > 8) return;
        setBarcode(value);
    }

    function handleKeyDown(e) {
        if (isPending('scan')) return;
        if (e.key === 'Enter' && barcode.length >= 6 && barcode.length <= 8) handleSubmitBarcode(barcode);
    }

    function handleSubmitBarcode(barcode) {
        // Read the run from storage, not state: the scanner-gun keydown listener is
        // registered once with [] deps, so its closure still holds the mount values.
        const locationSet = localStorage.getItem('location');
        if (!locationSet) return setShowChangeLocation(true);

        const modeSet = localStorage.getItem('scanMode');
        const categorySet = localStorage.getItem('productType');
        // Nothing is recorded until the clerk has said what they are counting - a
        // scanner gun on the counter must not scan into a run that does not exist.
        // '' is the uncategorized scope, a real choice; only null means unchosen.
        if (!modeSet || (modeSet === 'category' && categorySet == null)) return;

        async function submitScan() {
            try {
                const response = await fetchResource({ resource: 'inventory-check/scan-product', body: { location: locationSet, barcode, productType: categorySet ?? undefined }, loadingKey: 'scan' });

                if (!response.product) throw new Error('Erro ao buscar informaçoes do produto. Contactar o suporte.');

                // Out of scope short-circuits: nothing was recorded, and the clerk keeps scanning.
                if (response.outOfScope) {
                    setOutOfScope({
                        scanned: response.product.productType,
                        run: categorySet,
                    });
                    setAlreadyScanned(false);
                    setProductHasMultiple(false);
                    setShowBarcodeEntry(true);
                    setBarcode('');
                    setShowScannedProduct(true);
                    setLoadingImage(true);
                    setScannedProduct(response.product);
                    return;
                }

                setOutOfScope(null);

                if (response.message) {
                    setShowBarcodeEntry(false);
                    if (response.message.title === 'Already Scanned') {
                        setAlreadyScanned(true);
                    } else if (response.message.title === 'Multiple') {
                        setProductHasMultiple(true);
                        setMultipleQuantity(String(response.product.quantity));
                    }
                }
                // No success toast. The `counted` band on the verdict card is the
                // signal now, and it persists until the next scan instead of
                // vanishing after five seconds.

                setBarcode('');
                setShowScannedProduct(true);
                if (scannedProduct.barcode == response.product.barcode) return;
                setLoadingImage(true);
                setScannedProduct(response.product);
            } catch (error) {
                reset();
            }
        }
        submitScan();
    }

    function handleImageClick(imageUrl) {
        setImage(imageUrl);
        setShowImage(true);
    }

    function reset() {
        setBarcode('');
        setScannedProduct(defaultProduct);
        setShowScannedProduct(false);
        setShowBarcodeEntry(true);
        setAlreadyScanned(false);
        setProductHasMultiple(false);
        setMultipleQuantity('0');
        setCountingMore(false);
        setShowCompleted(false);
        setFinishedScanning(false);
        setLoadingImage(false);
        setOutOfScope(null);
        localStorage.setItem('finishedScanning', 'false');
    }
    
    async function handleSetQuantity(quantity) {
        try {
            const response = await fetchResource({
                resource: 'inventory-check/update-product-quantity',
                blocking: true,
                body: {
                    location,
                    barcode: finishedScanning ? unscannedProducts[0].barcode : scannedProduct.barcode,
                    quantity: parseInt(quantity)
                }
            });

            if (response.error) throw new Error(response.error);
            if (response.result !== 'OK') throw new Error('Erro ao atualizar quantidade do produto. Contactar o suporte.');
            setAlertMessage({ message: t('inventoryCheck.quantityUpdated'), type: 'success' });
            setShowAlert(true);

            if (finishedScanning) {
                if (unscannedProducts.length === 1) handleCompletedVerification();
                setUnscannedProducts(prev => prev.slice(1));
                setLoadingImage(true);
                return;
            };

            reset();
        } catch (error) {
            console.error(error);
        }
    }

    // Seeded at the CURRENT quantity, never current + 1. Pressing "there are more"
    // says that there are more; it does not say how many, and a blind confirm from
    // here has to be a no-op. Raising the number is the deliberate act.
    function handleCountMore() {
        // No `?? 0` default here: the API contract says quantity always arrives, but if
        // it ever didn't, '0' would pass isValidQuantity and leave the confirm live --
        // a reflexive tap would then write 0 and mark the piece missing, on the exact
        // screen this branch exists to stop that on. String(undefined) is 'undefined',
        // which isValidQuantity rejects, so the confirm stays disabled instead.
        setMultipleQuantity(String(scannedProduct.quantity));
        setCountingMore(true);
    }

    // The reviewer finds out a count ended from the person who ended it, rather than the
    // next time they happen to open the report. Fire-and-forget deliberately: the server
    // answers this before it builds anything, and a message that fails to send must never
    // cost the clerk the screen telling them they are done.
    function announceFinishedCount() {
        fetchResource({
            resource: 'inventory-check/finish-scanning',
            // undefined, not null: JSON drops the key entirely, which is how the server
            // tells the whole store from the uncategorized scope -- the same convention
            // verify-unscanned already uses.
            body: { location, productType: productType ?? undefined },
            loadingKey: 'finishScanning',
        }).catch(() => {});
    }

    function handleCompletedVerification() {
        setShowCompleted(true);
        announceFinishedCount();
    }

    async function handleFinishedScanning() {
        getUnscannedProducts();
        reset();
        setShowConfirmFinishedScanning(false);
        setFinishedScanning(true);
        localStorage.setItem('finishedScanning', 'true');
    }

    async function getUnscannedProducts() {
        try {
            const response = await fetchResource({resource: 'inventory-check/verify-unscanned', body: { location, productType: productType ?? undefined }, loadingKey: 'unscanned'});
            if (response.error) throw new Error(response.error);

            // Nothing left unscanned: the count is over without a walk-through at all.
            if (response.result === 'OK' && !response.products?.length) {
                setShowCompleted(true);
                return announceFinishedCount();
            }

            setUnscannedProducts(response.products);
            setUnscannedTotal(response.products.length);
        } catch (error) {
            setShowConfirmFinishedScanning(true);
            setAlertMessage({ message: t('inventoryCheck.unscannedFetchError'), type: 'error' });
        }
    }

    async function handleContinueScanning() {
        setShowCompleted(false);
        setFinishedScanning(false);
        localStorage.setItem('finishedScanning', 'false');
    }

    useEffect(() => {
        if (!location) return;
        localStorage.setItem('location', location);
    }, [location]);

    useEffect(() => {
        if (!isPending('scan') && !finishedScanning && showBarcodeEntry && !showCameraScanner) {
            inputRef.current?.focus();
        }
    });

    useEffect(() => {
        setShowBarcodeEntry(!finishedScanning);
        // A stored finishedScanning flag outlives the run it belonged to - every clerk
        // mid-count at deploy time has one, and no scanMode. Checking it against their
        // stale category would latch a completion screen onto whatever they pick next.
        if (finishedScanning && runEstablished) handleFinishedScanning();
    }, [finishedScanning]);

    useEffect(() => {
        const handleKeyDown = e => {
          // Checked first, before any buffer mutation: a volley arriving mid-request
          // must be dropped whole, not partially accumulated. Accumulating digits from
          // a blocked volley and only guarding the submit would leave those digits
          // sitting in the buffer to be prepended onto whatever the next legitimate
          // volley types, submitting a garbled barcode once the request settles.
          if (scanPendingRef.current) return;

          const inputIsFocused = inputRef.current && document.activeElement === inputRef.current;

          if (inputIsFocused) return;
      
          if (!/^[0-9]$/.test(e.key)) {
            if (e.key === 'Enter') {
              if (bufferRef.current.length >= 6 && bufferRef.current.length <= 8) handleSubmitBarcode(bufferRef.current);
              bufferRef.current = '';
            }
            return;
          }
      
          bufferRef.current += e.key;
      
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            bufferRef.current = '';
          }, 100);
        };
      
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        const storedLocation = localStorage.getItem('location');
        const storedMode = localStorage.getItem('scanMode');
        const storedCategory = localStorage.getItem('productType');
        setFinishedScanning(localStorage.getItem('finishedScanning') === 'true' ? true : false);
        if (!storedLocation) return setShowChangeLocation(true);
        setLocation(storedLocation);
        if (!storedMode) return setShowChangeMode(true);
        setScanMode(storedMode);
        // The whole store is a scope too, and its progress comes from the same endpoint.
        if (storedMode === 'all') {
            loadCategories();
            return;
        }
        // productType is seeded from storage above; only a null one is missing, and ''
        // is the uncategorized scope, which needs no prompt. The list is loaded either
        // way, because the header chip reads this scope's progress out of it.
        loadCategories();
        if (storedCategory == null) setShowChangeCategory(true);
    }, []);

    const maxContentWidth = '605px';

    // The scope is not a setting you occasionally change -- it is the thing you
    // check you got right before every scan. So it reads as state and is the
    // control at the same time: a chip that shows the current value and changes it.
    //
    // It replaces four bare 24px icons in a position:absolute row laid over a
    // centered title. The title grows with the location and the scope, so on a
    // narrow screen it wrapped straight underneath them and the two overlapped.
    const Header = () => (
        <Box sx={{ flex: 'none', mb: 1, width: '100%' }}>
            <PageTitle>{t('inventoryCheck.pageTitle')}</PageTitle>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                    icon={<LocationOnIcon />}
                    label={location ? LOCATIONS[location] : t('inventoryCheck.changeLocation')}
                    onClick={() => setShowChangeLocation(true)}
                    sx={{ height: 40, borderRadius: '999px' }}
                />
                <Chip
                    icon={<CategoryIcon />}
                    label={
                        scanMode === 'all'
                            ? `${t('inventoryCheck.allCategories')}${scopeProgress(null)}`
                            : `${scopeLabel(productType)}${scopeProgress(productType)}`
                    }
                    onClick={() => { loadCategories(); setShowChangeCategory(true); }}
                    sx={{ height: 40, borderRadius: '999px', maxWidth: '100%' }}
                />

                {/* Ending a run is not a scope change, so it stays a labelled
                    button rather than becoming a third chip -- but it sits in the
                    same wrapping row at the same height. Pushed to the right it
                    took a whole line to itself and read as the loudest thing on a
                    screen whose job is scanning. */}
                {finishedScanning ? (
                    <Button
                        variant='outlined'
                        color='inherit'
                        startIcon={<QrCodeScannerIcon />}
                        onClick={handleContinueScanning}
                        sx={{ height: 40, minHeight: 40 }}
                    >
                        {t('inventoryCheck.resumeScanning')}
                    </Button>
                ) : (
                    <Button
                        variant='outlined'
                        color='inherit'
                        startIcon={<TaskAltIcon />}
                        onClick={() => setShowConfirmFinishedScanning(true)}
                        sx={{ height: 40, minHeight: 40 }}
                    >
                        {t('inventoryCheck.finishScanning')}
                    </Button>
                )}
            </Box>
        </Box>
    )

    const ChangeLocationPopup = () => (
        <>{showChangeLocation && (
            <SelectLocation
                location={location}
                saveLocation={saveLocation}
                showChangeLocation={showChangeLocation}
                setShowChangeLocation={setShowChangeLocation}
            />
        )}</>
    )

    const ChangeCategoryPopup = () => (
        <>{showChangeCategory && (
            <SelectCategory
                productType={productType}
                categories={categories}
                isLoading={isPending('categories')}
                location={location}
                saveCategory={saveCategory}
                showChangeCategory={showChangeCategory}
                setShowChangeCategory={setShowChangeCategory}
            />
        )}</>
    )

    const ChangeModePopup = () => (
        <>{showChangeMode && (
            <SelectScanMode
                chooseMode={chooseMode}
                showChangeMode={showChangeMode}
                setShowChangeMode={setShowChangeMode}
            />
        )}</>
    )

    const NoRunSelected = () => (
        <Grid container direction='column' alignItems='center' justifyContent='center' height='100%' gap={2}>
            <Typography variant='h6'>{t('inventoryCheck.noCountSelected')}</Typography>
            <Button variant='contained' onClick={() => setShowChangeMode(true)}>
                {t('inventoryCheck.chooseWhatToCount')}
            </Button>
        </Grid>
    )

    // What the current verdict says and offers. Returning data rather than JSX keeps the
    // branching in one readable place and leaves ScanVerdict purely presentational.
    const verdictContent = () => {
        if (verdict === 'outOfScope') return {
            heading: t('inventoryCheck.outOfScopeHeading'),
            detail: (
                <>
                    <Trans i18nKey='inventoryCheck.outOfScopeItem' values={{ item: outOfScope.scanned || t('inventoryCheck.noCategoryItem') }} components={{ b: <b /> }} />{' '}
                    <Trans i18nKey='inventoryCheck.outOfScopeCountingScope' values={{ scope: scopeLabel(outOfScope.run) }} components={{ b: <b /> }} />{' '}
                    <b>{t('inventoryCheck.notRecorded')}</b>
                </>
            )
        };

        if (verdict === 'already') return {
            heading: t('inventoryCheck.alreadyCountedHeading'),
            detail: t('inventoryCheck.alreadyCountedDetail'),
            actions: (
                <ActionBar
                    // Skip is the confirm slot on purpose: it is the safe answer, so it
                    // is the one the thumb lands on. The old screen had this exactly
                    // backwards, with a green check on the button that writes.
                    confirm={{ label: t('inventoryCheck.skipKeepCounting'), onClick: reset }}
                    secondary={{ label: t('inventoryCheck.thereAreMore'), onClick: handleCountMore }}
                />
            )
        };

        if (verdict === 'countMore' || verdict === 'multiple') {
            const isCountMore = verdict === 'countMore';
            return {
                heading: isCountMore
                    ? t('inventoryCheck.countMoreHeading')
                    : t('inventoryCheck.multipleHeading', { count: scannedProduct.quantity }),
                detail: isCountMore ? t('inventoryCheck.countMoreDetail') : t('inventoryCheck.multipleDetail'),
                children: (
                    <Grid container justifyContent='center'>
                        <QuantityStepper value={multipleQuantity} onChange={setMultipleQuantity} />
                    </Grid>
                ),
                actions: (
                    <ActionBar
                        cancel={isCountMore ? { label: t('common.back'), onClick: () => setCountingMore(false) } : undefined}
                        confirm={{
                            label: t('inventoryCheck.confirmQuantity', { count: multipleQuantity }),
                            onClick: () => handleSetQuantity(multipleQuantity),
                            // The NaN guard, at the only place it can be enforced.
                            disabled: !isValidQuantity(multipleQuantity)
                        }}
                    />
                )
            };
        }

        return {
            heading: t('inventoryCheck.counted'),
            detail: t('inventoryCheck.countedDetail')
        };
    };

    const ScannedProduct = extraActions => {
        const content = verdictContent();
        return (
            <ScanVerdict
                verdict={verdict}
                product={scannedProduct}
                heading={content.heading}
                detail={content.detail}
                imageLoading={loadingImage}
                onImageLoad={() => setLoadingImage(false)}
                onImageClick={() => handleImageClick(scannedProduct.imageUrl)}
                actions={content.actions ?? extraActions}
            >
                {content.children}
            </ScanVerdict>
        );
    };

    // Fires on every barcode -- a scanner gun's own strobing overlay was the defect
    // this migration removed, so this stays small, non-blocking, and reserves its own
    // space rather than shifting the layout on toggle. It has to be reachable on every
    // screen the window-level scanner-gun listener can fire a scan from -- not just the
    // one with the visible TextField -- because that listener has no idea which screen
    // is showing. Before this it sat inside the barcode-entry block and went silent on
    // the Already-Scanned/Multiple screens and the whole unscanned-products step, where
    // master painted a full-screen overlay and this branch painted nothing at all.
    const ScanBusyIndicator = () => (
        <Grid item height='24px'>
            {isPending('scan') && <CircularProgress size={20} data-testid='scan-busy' />}
        </Grid>
    )

    const UnscannedProducts = () => {
        if (isPending('unscanned') || !unscannedProducts.length) return <InlineLoader />;

        return (
            <Grid
                container
                direction='column'
                justifyContent='center'
                alignItems='center'
                height='100%'
                gap={2}
                sx={{ maxWidth: maxContentWidth }}
            >
                <ScanVerdict
                    verdict='unscanned'
                    product={unscannedProducts[0]}
                    heading={t('inventoryCheck.notFoundYetHeading')}
                    detail={t('inventoryCheck.unscannedPosition', {
                        index: unscannedTotal - unscannedProducts.length + 1,
                        total: unscannedTotal
                    })}
                    imageLoading={loadingImage}
                    onImageLoad={() => setLoadingImage(false)}
                    onImageClick={() => handleImageClick(unscannedProducts[0].imageUrl)}
                    actions={
                        /* Not an ActionBar. Its confirm slot is always `contained`, so any
                           pair built from it has a loudest button -- and whichever one that
                           is becomes the reflex press. Neither answer here is safe. This is
                           SelectScanMode's pattern: "these are the choice, not an answer to
                           it", full width, stacked, equal weight. */
                        <Stack spacing={1.5} width='100%'>
                            <Button
                                fullWidth
                                variant='outlined'
                                color='inherit'
                                onClick={() => handleSetQuantity(1)}
                                sx={{ minHeight: 48 }}
                            >
                                {t('inventoryCheck.itsHere')}
                            </Button>
                            <Button
                                fullWidth
                                variant='outlined'
                                color='error'
                                onClick={() => handleSetQuantity(0)}
                                sx={{ minHeight: 48 }}
                            >
                                {t('inventoryCheck.notFoundMarkMissing')}
                            </Button>
                        </Stack>
                    }
                >
                    <Typography variant='body2' color='text.secondary' textAlign='center'>
                        {t('inventoryCheck.missingGoesToReport')}
                    </Typography>
                </ScanVerdict>
                <ScanBusyIndicator />
            </Grid>
        )
    }

    const Scan = () => (
        <Grid
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            height='100%'
            gap={2}
            sx={{ maxWidth: maxContentWidth }}
        >
            {/* While the camera scanner is open, ScannedProduct() renders inside its
                children instead (see below) - rendering it here too would put the same
                product on screen twice, once behind the camera view. */}
            {!showCameraScanner && (showScannedProduct ? ScannedProduct() : (
                <Grid item>
                    <Typography variant='h6'>{t('inventoryCheck.scanNextProduct')}</Typography>
                </Grid>
            ))}
            {showBarcodeEntry && (
                <Grid item container direction='column' alignItems='center' gap={1}>
                    <TextField
                        label={t('inventoryCheck.barcodeLabel')}
                        value={barcode || ''}
                        onChange={handleBarcodeInput}
                        onKeyDown={handleKeyDown}
                        inputRef={inputRef}
                        focused
                    />
                </Grid>
            )}
            {isCameraAvailable() && (
                <Grid item>
                    <Button
                        variant='outlined'
                        aria-label={t('common.scan')}
                        onClick={() => setShowCameraScanner(true)}
                        sx={{ gap: '.3rem' }}
                    >
                        <QrCodeScannerIcon fontSize='large' />
                        {t('inventoryCheck.scanWithCamera')}
                    </Button>
                </Grid>
            )}
            <ScanBusyIndicator />
        </Grid>
    )

    const ImagePopup = () => (
        <Lightbox
            open={showImage}
            onClose={() => setShowImage(false)}
            src={image}
            alt='product'
        />
    )

    const ConfirmFinishedScanning = () => {
        return (
            <Sheet
                open={showConfirmFinishedScanning}
                onClose={() => setShowConfirmFinishedScanning(false)}
                actions={
                    <ActionBar
                        cancel={{ label: t('common.cancel'), onClick: () => setShowConfirmFinishedScanning(false) }}
                        confirm={{ label: t('inventoryCheck.finish'), onClick: handleFinishedScanning }}
                    />
                }
            >
                <Typography variant='h6'>{t('inventoryCheck.confirmScannedAll')}</Typography>
            </Sheet>
        )
    }

    const Completed = () => (
        <Grid container direction='column' alignItems='center' justifyContent='center' height='100%'>
            <Box sx={{
                borderRadius: '12px',
                border: `2px solid ${tokens.success}`,
                bgcolor: tokens.successSoft,
                px: 3,
                py: 2.5
            }}>
                <Typography sx={{ color: tokens.success, fontWeight: 600 }}>
                    {t('inventoryCheck.completed')}
                </Typography>
            </Box>
        </Grid>
    )

    const AlertMessage = () => <Alert className='alert' severity={alertMessage.type} variant='filled'>{alertMessage.message}</Alert>

    return (<>
        {showImage && ImagePopup()}
        {showConfirmFinishedScanning && ConfirmFinishedScanning()}
        {showCameraScanner && (
            <BarcodeScanner
                open={showCameraScanner}
                onClose={() => setShowCameraScanner(false)}
                onScan={handleSubmitBarcode}
                continuous
                // showBarcodeEntry is already the page's own "ready for the next
                // barcode" signal - it goes false exactly when Already Scanned or
                // Multiple needs a decision, and stays true for Out of scope, which
                // the code deliberately treats as keep-scanning. showScannedProduct
                // adds the per-item verification pause. Deriving from these means any
                // future interruption state pauses the camera for free.
                paused={!showBarcodeEntry || isPending('scan') || showScannedProduct}
                title={t('inventoryCheck.pageTitle')}
            >
                {/* Mutually exclusive with the ScannedProduct() rendered in Scan() above
                    (guarded there by !showCameraScanner) - that guard is what stops the
                    product from being rendered twice, once behind the camera view. */}
                {showScannedProduct && ScannedProduct(
                    // Only when the verdict asks nothing of the clerk. A verdict with its
                    // own actions supplies them itself.
                    verdict === 'counted' || verdict === 'outOfScope' ? (
                        <ActionBar confirm={{ label: t('inventoryCheck.scanNext'), onClick: reset }} />
                    ) : undefined
                )}
            </BarcodeScanner>
        )}
        <Page alignItems='center'>
            {showAlert && AlertMessage()}
            {Header()}
            {ChangeLocationPopup()}
            {ChangeModePopup()}
            {ChangeCategoryPopup()}
            {!runEstablished ? NoRunSelected()
              : !showCompleted ? (!finishedScanning ? Scan() : UnscannedProducts())
              : (Completed())}
        </Page>
    </>)
}
